import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import CONFIG from "../../config/config";
import { progressItems } from "../../config/progressItems";
import { Card, CardBody, CardHeader } from "../../components/card/card";
import QuizImplementor from "../../components/QuizImplementor/QuizImplementor";
import { getFlagEmoji, getAuthHeaders } from "../../misc.js";


// API helper functions with token attached
const fetchQuizTypes = async () => {
  const response = await fetch(`${CONFIG.API_URL}/quiz/types`, {
    headers: { ...getAuthHeaders() }
  });
  return response.json();
};

const fetchChannels = async () => {
  const response = await fetch(`${CONFIG.API_URL}/channel`, {
    headers: { ...getAuthHeaders() }
  });
  return response.json();
};

const fetchQuizById = async (id) => {
  const response = await fetch(`${CONFIG.API_URL}/quiz/get/${id}`, {
    headers: { ...getAuthHeaders() }
  });
  return response.json();
};

const fetchQuizList = async () => {
  const response = await fetch(`${CONFIG.API_URL}/quiz/list/ready`, {
    headers: { ...getAuthHeaders() }
  });
  return response.json();
};

// Endpoint to immediately create a quiz with "structuring" status
const initiateStructureGeneration = async (data) => {
  const response = await fetch(`${CONFIG.API_URL}/generate/structure`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  return response.json();
};

const generateTranslation = async (quizId, channel, language) => {
  const response = await fetch(`${CONFIG.API_URL}/generate/translation`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ quizId, channel, language }),
  });
  return response.json();
};

const createQuiz = async (data) => {
  const response = await fetch(`${CONFIG.API_URL}/quiz/create`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify(data),
  });
  return response.json();
};

const QuizCreate = () => {
  const { id } = useParams(); // for edit mode
  const navigate = useNavigate();

  // Option for quiz creation: "new" or "translation"
  const [creationOption, setCreationOption] = useState("new");

  // States for quiz details
  const [selectedType, setSelectedType] = useState("");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [channels, setChannels] = useState([]);
  const [quizTitle, setQuizTitle] = useState("");
  const [quizStatus, setQuizStatus] = useState("");
  const [quizStructure, setQuizStructure] = useState(null);
  const [quizTypes, setQuizTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isStructureGenerated, setIsStructureGenerated] = useState(false);
  const [isGeneratingStructure, setIsGeneratingStructure] = useState(false);
  const [quizId, setQuizId] = useState("");

  // States for translation mode
  const [quizList, setQuizList] = useState([]);
  const [selectedTranslationQuizId, setSelectedTranslationQuizId] = useState("");

  const [currentProgressIndex, setCurrentProgressIndex] = useState(0);

  // Ref for WebSocket connection
  const wsRef = useRef(null);

  // Open WebSocket connection when component mounts
  useEffect(() => {
    // Determine protocol: use 'wss:' when on HTTPS.
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    // Use the intended API host.
    const wsHost = "api.moatoverflow.com";
    // Construct the URL, assuming the WebSocket path is '/ws'
    const wsUrl = `${wsProtocol}//${wsHost}/ws`;
    wsRef.current = new WebSocket(wsUrl);
    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        // When the backend notifies that the quiz structure is complete...
        if (data.type === "quiz-structured" && data.quizId === quizId) {
          setQuizStructure(data.structure);
          setQuizStatus("draft")
          setSelectedType(data.quizType);
          setIsStructureGenerated(true);
        } else if (data.type === "quiz-structure-error" && data.quizId === quizId) {
          setQuizStatus("failed_structuring");
          console.error("Structure generation error:", data.error);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    };

    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, [quizId]);

  // Rotate progress items every 5 seconds while structuring
  useEffect(() => {
    let interval;
    if (!isStructureGenerated && quizId) {
      interval = setInterval(() => {
        setCurrentProgressIndex((prev) => (prev + 1) % progressItems.length);
      }, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isStructureGenerated, quizId, progressItems.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [responseTypes, responseChannels] = await Promise.all([
          fetchQuizTypes(),
          fetchChannels(),
        ]);
        setQuizTypes(responseTypes.types);
        setChannels(responseChannels.data);

        // If editing an existing quiz, load its data.
        if (id) {
          const quizData = await fetchQuizById(id);
          setQuizTitle(quizData.title);
          setSelectedType(quizData.type);
          setSelectedChannel(quizData.channel._id);
          setQuizStructure(quizData.structure);
          setQuizId(quizData._id);
          setIsGeneratingStructure(quizData.status ? quizData.status === "structuring" : false);
          // Check if structure is non-empty (or use quizData.status if available)
          setIsStructureGenerated(
            quizData.structure &&
            Object.keys(quizData.structure).length > 0
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Load quiz list for translation mode if needed.
  const fetchQuizListIfNeeded = async () => {
    if (quizList.length === 0) {
      try {
        const listResponse = await fetchQuizList();
        setQuizList(
          (listResponse.data.filter((quiz) => quiz.channel.language === "en") || [])
        );
      } catch (error) {
        console.error("Error fetching quiz list:", error);
      }
    }
  };

  // Handler for generating structure (or translation)
  const handleGenerateStructure = async () => {
    try {
      setIsGeneratingStructure(true);
      const channelObject = channels.find((c) => c._id === selectedChannel);
      let responseData;

      if (creationOption === "new") {
        responseData = await initiateStructureGeneration({
          title: quizTitle,
          quizType: selectedType,
          channel: channelObject,
          language: channelObject.language,
        });
      } else if (creationOption === "translation") {
        responseData = await generateTranslation(
          selectedTranslationQuizId,
          channelObject,
          channelObject.language
        );
      }

      setQuizId(responseData._id);
      navigate(`/create-quiz/${responseData._id}`);
      // The WebSocket listener will update quizStructure and selectedType when complete.
    } catch (error) {
      console.error("Error initiating structure generation:", error);
    } finally {
      setIsGeneratingStructure(false);
    }
  };

  // Handler to create quiz if needed.
  const handleCreateQuiz = async (finalStructure) => {
    try {
      const response = await createQuiz({
        ...finalStructure,
        _id: quizId,
        title: quizTitle,
      });
      console.log("Quiz created successfully:", response);
    } catch (error) {
      console.error("Error creating quiz:", error);
    }
  };

  // Disable the generate structure button until required inputs are filled.
  const shouldDisableStructureButton = () => {
    if (creationOption === "new") {
      return quizTitle === "" || selectedType === "" || selectedChannel === "";
    } else if (creationOption === "translation") {
      return selectedTranslationQuizId === "" || selectedChannel === "";
    }
    return true;
  };

  // Handler for when a quiz is selected for translation.
  const handleTranslationQuizSelect = (e) => {
    const quizIdSelected = e.target.value;
    setSelectedTranslationQuizId(quizIdSelected);
    const selectedQuiz = quizList.find((quiz) => quiz._id === quizIdSelected);
    if (selectedQuiz) {
      setQuizTitle(selectedQuiz.title);
      setSelectedType(selectedQuiz.type);
    } else {
      setQuizTitle("");
      setSelectedType("");
    }
  };

  // When in translation mode, filter out channels that are in English or the original quiz channel.
  const availableChannels =
    creationOption === "translation" && selectedTranslationQuizId
      ? channels.filter((channel) => {
          const selectedQuiz = quizList.find(
            (quiz) => quiz._id === selectedTranslationQuizId
          );
          return (
            selectedQuiz &&
            selectedQuiz.channel._id !== channel._id &&
            channel.language !== "en"
          );
        })
      : channels.filter((channel) => channel.language !== "en");

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      {/* Option selection (only shown if not editing and structure not generated) */}
      {!id && !isStructureGenerated && (
        <Card className="mb-3">
          <CardHeader>
            <h2>Select Quiz Creation Option</h2>
          </CardHeader>
          <CardBody>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="creationOption"
                id="creationOptionNew"
                value="new"
                checked={creationOption === "new"}
                onChange={(e) => setCreationOption(e.target.value)}
              />
              <label className="form-check-label" htmlFor="creationOptionNew">
                Create a new quiz for a channel
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="creationOption"
                id="creationOptionTranslation"
                value="translation"
                checked={creationOption === "translation"}
                onChange={(e) => {
                  setCreationOption(e.target.value);
                  fetchQuizListIfNeeded();
                }}
              />
              <label className="form-check-label" htmlFor="creationOptionTranslation">
                Create translation from English quiz
              </label>
            </div>
          </CardBody>
        </Card>
      )}

      <Card>
        <CardHeader>
          <h1>Create Quiz</h1>
        </CardHeader>
        <CardBody>
          {isStructureGenerated ? (
            // Display general settings once structure is generated
            <div className="accordion mb-3" id="generalSettingsAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header" id="headingGeneralSettings">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#collapseGeneralSettings"
                    aria-expanded="false"
                    aria-controls="collapseGeneralSettings"
                  >
                    General Settings
                  </button>
                </h2>
                <div
                  id="collapseGeneralSettings"
                  className="accordion-collapse collapse"
                  aria-labelledby="headingGeneralSettings"
                  data-bs-parent="#generalSettingsAccordion"
                >
                  <div className="accordion-body">
                    <div className="mb-2">
                      <label>Quiz Title</label>
                      <input
                        type="text"
                        className="form-control"
                        value={quizTitle}
                        onChange={(e) => setQuizTitle(e.target.value)}
                        placeholder="Enter Quiz Title"
                        disabled={true}
                      />
                    </div>
                    <div className="mb-2">
                      <div className="row">
                        <div className="col-xl-5">
                          <label>Quiz Type</label>
                          <select
                            className="form-select"
                            value={selectedType}
                            onChange={(e) => setSelectedType(e.target.value)}
                            disabled={true}
                          >
                            <option value="">Select Quiz Type</option>
                            {quizTypes.map((type) => (
                              <option key={type.type} value={type.type}>
                                {type.title}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-xl-5">
                          <label>Channel</label>
                          <select
                            className="form-select"
                            value={selectedChannel}
                            onChange={(e) => setSelectedChannel(e.target.value)}
                            disabled={true}
                          >
                            <option value="">Select Channel</option>
                            {channels.map((channel) => (
                              <option key={channel._id} value={channel._id}>
                                {channel.name} {getFlagEmoji(channel.language)}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Inputs when structure is not generated
            <>
              {creationOption === "new" ? (
                <>
                  <div className="mb-2">
                    <label>Quiz Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={quizTitle}
                      onChange={(e) => setQuizTitle(e.target.value)}
                      placeholder="Enter Quiz Title"
                      disabled={isGeneratingStructure}
                    />
                  </div>
                  <div className="mb-2">
                    <div className="row">
                      <div className="col-xl-5">
                        <label>Quiz Type</label>
                        <select
                          className="form-select"
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                          disabled={isGeneratingStructure}
                        >
                          <option value="">Select Quiz Type</option>
                          {quizTypes.map((type) => (
                            <option key={type.type} value={type.type}>
                              {type.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xl-5">
                        <label>Channel</label>
                        <select
                          className="form-select"
                          value={selectedChannel}
                          onChange={(e) => setSelectedChannel(e.target.value)}
                          disabled={isGeneratingStructure}

                        >
                          <option value="">Select Channel</option>
                          {channels.map((channel) => (
                            <option key={channel._id} value={channel._id}>
                              {channel.name} {getFlagEmoji(channel.language)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xl-2 d-flex align-items-end">
                        <button
                          disabled={
                            shouldDisableStructureButton() ||
                            isGeneratingStructure
                          }
                          onClick={handleGenerateStructure}
                          className="btn btn-success w-100"
                        >
                          {isGeneratingStructure ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              &nbsp;Generating...
                            </>
                          ) : (
                            "Generate Structure"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                // Translation mode inputs
                <>
                  <div className="mb-2">
                    <div className="row">
                      <div className="col-xl-5">
                        <label>Select Quiz for Translation</label>
                        <select
                          className="form-select"
                          value={selectedTranslationQuizId}
                          onChange={handleTranslationQuizSelect}
                        >
                          <option value="">Select Quiz</option>
                          {quizList.map((quiz) => (
                            <option key={quiz._id} value={quiz._id}>
                              {quiz.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xl-5">
                        <label>Channel</label>
                        <select
                          className="form-select"
                          value={selectedChannel}
                          onChange={(e) => setSelectedChannel(e.target.value)}
                        >
                          <option value="">Select Channel</option>
                          {availableChannels.map((channel) => (
                            <option key={channel._id} value={channel._id}>
                              {channel.name} {getFlagEmoji(channel.language)}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-xl-2 d-flex align-items-end">
                        <button
                          disabled={
                            shouldDisableStructureButton() ||
                            isGeneratingStructure
                          }
                          onClick={handleGenerateStructure}
                          className="btn btn-success w-100"
                        >
                          {isGeneratingStructure ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              &nbsp;Generating...
                            </>
                          ) : (
                            "Generate Structure Translation"
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label>Quiz Title</label>
                    <input
                      type="text"
                      className="form-control"
                      value={quizTitle}
                      disabled
                      placeholder="Quiz title will be auto-filled"
                    />
                  </div>
                </>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* Extra progress card shown while structuring */}
      {quizStatus === "failed_structuring" && quizId && (
        <Card className="mt-3">
          <CardBody>
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ height: "500px" }}
            >
              <i
                className="fas fa-times-circle text-danger"
                style={{ fontSize: "96px" }}
              ></i>
              <div
                className="mt-2 text-center"
                style={{ fontSize: "48px", fontWeight: 500 }}
              >
                <span>
                  Failed to generate the quiz structure, please try again
                </span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {!isStructureGenerated && quizId && quizStatus !== "failed_structuring" && (
        <Card className="mt-3">
          <CardBody>
            <div
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ height: "500px" }}
            >
              <i
                className={progressItems[currentProgressIndex].icon}
                style={{ fontSize: "96px" }}
              ></i>
              <div
                className="mt-2 text-center"
                style={{ fontSize: "48px", fontWeight: 500 }}
              >
                <span>{progressItems[currentProgressIndex].message}</span>
              </div>
              <div className="mt-2 text-center">
                <span>Feel free to leave this page—your structure is still generating in the background.</span>
              </div>
            </div>
          </CardBody>
        </Card>
      )}
      <QuizImplementor 
        channel={channels.find((c) => c._id === selectedChannel)}
        quizStructure={quizStructure}
        selectedType={selectedType}
        quizId={quizId}
        onCreateQuiz={handleCreateQuiz}
      />
    </>
  );
};

export default QuizCreate;
